import cloneDeep from 'lodash-es/cloneDeep';
import get from 'lodash-es/get';
import set from 'lodash-es/set';
import { CallRouteV1 } from '@frontend/api-call-route';
import { iterateProperties } from '../utils';

/**
 * This function reconciles current editing nodes with the latest and previous nodes
 * from the backend (BE). This function ensures that only the properties that have changed on the backend
 * are updated in the current editing state, while preserving the rest of the data.
 *
 * @param {Object} params - The parameters for the reconciliation process.
 * @param {Node[]} params.tempEditingNodes - The current state of the nodes in their editing state.
 * @param {Node[]} params.latestNodes - The latest state of the nodes from the backend.
 * @param {Node[]} params.prevNodes - The previous state of the nodes from the backend.
 * @returns {Node[]} - A new array of nodes with reconciled data.
 *
 * @remarks
 * - Only properties that have changed on the backend (i.e., differ from the previous state) are updated.
 * - The function ensures immutability by cloning the current node before applying updates.
 *
 * @example
 * const reconciledNodes = reconcileGraphNodes({
 *   tempEditingNodes: editingNodes,
 *   latestNodes: backendLatestNodes,
 *   prevNodes: backendPrevNodes,
 * });
 */
export const reconcileGraphNodes = ({
  tempEditingNodes,
  latestNodes,
  prevNodes,
}: {
  tempEditingNodes: CallRouteV1.Types.Node[];
  latestNodes: CallRouteV1.Types.Node[];
  prevNodes: CallRouteV1.Types.Node[];
}): CallRouteV1.Types.Node[] => {
  const newNodes = [...tempEditingNodes];

  // Go through every node in the editing state of the callroute and update it with the latest state of the node from the
  // BE.
  for (let i = 0; i < newNodes.length; i++) {
    const currentNode = newNodes[i]; // The current node in the callroute (this is the current state of the node in it's editing state)
    const latestNode = latestNodes.find((node) => node.id === currentNode.id); // the latest state of the node from the BE
    const prevNode = prevNodes.find((node) => node.id === currentNode.id); // the previous state of the node from the BE

    // First Check if the currentNode is the same type as the latest Node, if not, skip this since that means the node
    // has changed type so the data should be different.
    if (latestNode && currentNode.type === latestNode.type) {
      // Clone the currentNode object so we don't mutate the original object (or it's nested objects)
      const updatedNode = cloneDeep(currentNode);
      // Go through every property of the currentNode object and update it with the latestNode object properties,
      // but only if the latestNode object property has changed from the previousNode object property since this means that
      // the value has changed on the BE from what we previously had.
      iterateProperties(currentNode, (_, currentValue, path) => {
        const latestValue = get(latestNode, path);
        const prevValue = get(prevNode, path);
        if (currentValue !== latestValue && latestValue !== prevValue) {
          set(updatedNode, path, latestValue);
        }
      });
      newNodes[i] = updatedNode;
    }
  }

  return newNodes;
};

/**
 * Helper function that handles any processing on the nodes before submitting them to be saved in the backend.
 *
 * @param {Node[]} nodes - The array of nodes to process.
 * @returns {Node[]} - The processed array of nodes.
 */
export const processNodesForSave = (nodes: CallRouteV1.Types.Node[]): CallRouteV1.Types.Node[] => {
  const updatedNodes = nodes.map((node) => {
    // We need to empty (set to empty string) any scheduleIds from any break schedule rules in the OfficeHour nodes
    // expansion data that were newly generated on the FE so that the BE knows that these are new break schedules and
    // not existing ones. ScheduleIds that have been generated on the FE will start with the prefix 'new:'.
    if (node.type === CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS) {
      return {
        ...node,
        callObject: {
          ...node.callObject,
          phoneHoursExpansion: {
            phoneHours: node.callObject.phoneHoursExpansion?.phoneHours.map((phoneHour) => ({
              ...phoneHour,
              scheduleId: phoneHour.scheduleId.startsWith('new:') ? '' : phoneHour.scheduleId,
            })),
          },
        },
      };
    }

    // We need to empty any FE generated primitiveIds from the callObject for any BreakNodes (same logic as OfficeHours node above).
    if (node.type === CallRouteV1.Types.NodeType.NODE_TYPE_BREAK_HOURS) {
      if (node.callObject?.primitiveId?.startsWith('new:')) {
        return {
          ...node,
          callObject: {
            ...node.callObject,
            primitiveId: '',
          },
        };
      }
    }

    return node;
  });

  return updatedNodes;
};
